import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { normalizeSectionId } from '@theme/section-renderer';
import { requestYieldCallback, getViewParameterValue } from '@theme/utilities';

const FAST_VARIANT_CACHE_LIMIT = 24;
const FAST_VARIANT_PREFETCH_CAP = 2;

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  /** @type {boolean} */
  #listenersBound = false;

  /** @type {Map<string, Promise<string>>} */
  #responseCache = new Map();

  /** @type {number} */
  #prefetchInFlight = 0;

  /** @type {string | undefined} */
  #latestRequestUrl;

  connectedCallback() {
    super.connectedCallback();
    this.#syncRadioState();

    if (!this.#listenersBound) {
      this.#listenersBound = true;
      this.addEventListener('change', this.variantChanged);
      if (this.#isFastVariant()) {
        this.addEventListener('pointerenter', this.#onPrefetchIntent, true);
        this.addEventListener('focusin', this.#onPrefetchIntent);
      }
    }
  }

  updatedCallback() {
    super.updatedCallback();
    this.#syncRadioState();
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged = (event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);

    if (this.#isFastVariant() && event.target instanceof HTMLInputElement) {
      this.applyFastVariantDependencies();
    }

    const urlOption = this.#urlOptionForHistory(selectedOption);

    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.#setPending(true);
    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct);

    const url = new URL(window.location.href);

    const variantId = urlOption.dataset.variantId || selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  };

  /**
   * Selects a radio without fetching, then fetches once with dependent options applied.
   * Used by product-v2 Add Frame (White) so it does not chain a second click.
   * @param {HTMLInputElement} input
   */
  selectAndFetch(input) {
    if (!(input instanceof HTMLInputElement) || input.disabled) return;

    this.selectWithoutFetch(input);
    if (this.#isFastVariant()) this.applyFastVariantDependencies();

    this.dispatchEvent(new VariantSelectedEvent({ id: input.dataset.optionValueId ?? '' }));
    this.#setPending(true);
    this.fetchUpdatedSection(this.buildRequestUrl(input), false);

    const urlOption = this.#urlOptionForHistory(input);
    const variantId = urlOption.dataset.variantId || input.dataset.variantId;
    if (!variantId || this.dataset.templateProductMatch !== 'true') return;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Updates the selected option without dispatching change or fetching.
   * @param {HTMLInputElement} input
   */
  selectWithoutFetch(input) {
    if (!(input instanceof HTMLInputElement) || input.disabled) return;
    this.updateSelectedOption(input);
  }

  /**
   * Applies Postcard → Unframed and Unframed → No mat without fetching.
   */
  applyFastVariantDependencies() {
    const sizeChecked = this.querySelector('[data-option-type="size"] input:checked');
    if (sizeChecked instanceof HTMLInputElement && sizeChecked.dataset.sizePostcard === 'true') {
      const unframed = this.querySelector('[data-option-type="frame"] input[data-frame-unframed="true"]');
      if (unframed instanceof HTMLInputElement && !unframed.checked && !unframed.disabled) {
        this.selectWithoutFetch(unframed);
      }
    }

    const frameChecked = this.querySelector('[data-option-type="frame"] input:checked');
    if (frameChecked instanceof HTMLInputElement && frameChecked.dataset.frameUnframed === 'true') {
      const noMat = this.querySelector('[data-option-type="mat"] input[data-mat-value="no"]');
      if (noMat instanceof HTMLInputElement && !noMat.checked && !noMat.disabled) {
        this.selectWithoutFetch(noMat);
      }
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
            fieldset.style.setProperty(
              '--pill-width-current',
              `${radios[newCurrentIndex].parentElement?.offsetWidth || 0}px`
            );
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
            fieldset.style.setProperty(
              '--pill-width-previous',
              `${radios[newPreviousIndex].parentElement?.offsetWidth || 0}px`
            );
          }
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }

    if (this.#isFastVariant() && this.dataset.sectionId) {
      params.push(`section_id=${normalizeSectionId(this.dataset.sectionId)}`);
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    this.#latestRequestUrl = requestUrl;

    const cached = this.#isFastVariant() ? this.#responseCache.get(requestUrl) : undefined;

    if (!cached) {
      this.#abortController?.abort();
      this.#abortController = new AbortController();
    }

    const htmlPromise = cached || this.#fetchHtml(requestUrl, cached ? undefined : this.#abortController?.signal);

    if (this.#isFastVariant() && !cached) {
      this.#storeCache(requestUrl, htmlPromise);
    }

    htmlPromise
      .then((responseText) => {
        if (this.#latestRequestUrl !== requestUrl) return;

        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) {
          this.#setPending(false);
          return;
        }

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }

        this.#setPending(false);
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
          return;
        }
        console.error(error);
        if (this.#latestRequestUrl === requestUrl) this.#setPending(false);
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }

  #isFastVariant() {
    return this.hasAttribute('data-fast-variant');
  }

  #syncRadioState() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    this.#radios = [];
    this.#checkedIndices = [];

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });
  }

  /**
   * @param {HTMLElement} selectedOption
   * @returns {HTMLElement}
   */
  #urlOptionForHistory(selectedOption) {
    if (!this.#isFastVariant()) return selectedOption;
    const mat = this.querySelector('[data-option-type="mat"] input:checked');
    if (mat instanceof HTMLInputElement && mat.dataset.variantId) return mat;
    const frame = this.querySelector('[data-option-type="frame"] input:checked');
    if (frame instanceof HTMLInputElement && frame.dataset.variantId) return frame;
    return selectedOption;
  }

  /**
   * @param {boolean} pending
   */
  #setPending(pending) {
    if (!this.#isFastVariant()) return;

    const section = this.closest('.shopify-section');
    const price = section?.querySelector('product-price');
    const atc = section?.querySelector('.buy-buttons-block [ref="addToCartButton"]');

    if (pending) {
      this.setAttribute('aria-busy', 'true');
      price?.setAttribute('data-pending', '');
      atc?.setAttribute('data-pending', '');
      return;
    }

    this.removeAttribute('aria-busy');
    price?.removeAttribute('data-pending');
    atc?.removeAttribute('data-pending');
  }

  /**
   * @param {Event} event
   */
  #onPrefetchIntent = (event) => {
    if (!this.#isFastVariant()) return;
    const target = event.target;
    if (!(target instanceof Element)) return;

    const input =
      target instanceof HTMLInputElement
        ? target
        : target.closest('label')?.querySelector('input[type="radio"]');

    if (!(input instanceof HTMLInputElement) || !this.contains(input)) return;
    if (input.disabled || input.checked) return;

    this.#prefetchInput(input);
  };

  /**
   * @param {HTMLInputElement} input
   */
  #prefetchInput(input) {
    if (this.#prefersReducedData()) return;
    if (this.#prefetchInFlight >= FAST_VARIANT_PREFETCH_CAP) return;

    const requestUrl = this.#requestUrlForHypothetical(input);
    if (!requestUrl || this.#responseCache.has(requestUrl)) return;

    this.#prefetchInFlight += 1;
    const htmlPromise = this.#fetchHtml(requestUrl);
    this.#storeCache(requestUrl, htmlPromise);
    htmlPromise.finally(() => {
      this.#prefetchInFlight = Math.max(0, this.#prefetchInFlight - 1);
    });
  }

  /**
   * @returns {boolean}
   */
  #prefersReducedData() {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (connection?.saveData) return true;
    return window.matchMedia?.('(prefers-reduced-data: reduce)')?.matches === true;
  }

  /**
   * @param {HTMLInputElement} intendedInput
   * @returns {string | undefined}
   */
  #requestUrlForHypothetical(intendedInput) {
    const optionValueIds = this.#optionValuesForHypothetical(intendedInput);
    if (!optionValueIds.length) return undefined;

    let productUrl = intendedInput.dataset.connectedProductUrl || this.dataset.productUrl;
    if (!productUrl) return undefined;

    const params = [];
    const viewParamValue = getViewParameterValue();
    if (viewParamValue) params.push(`view=${viewParamValue}`);
    params.push(`option_values=${optionValueIds.join(',')}`);
    if (this.dataset.sectionId) {
      params.push(`section_id=${normalizeSectionId(this.dataset.sectionId)}`);
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * @param {HTMLInputElement} intendedInput
   * @returns {string[]}
   */
  #optionValuesForHypothetical(intendedInput) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    /** @type {HTMLInputElement[]} */
    let selected = [];

    for (const fieldset of fieldsets) {
      const radios = Array.from(fieldset.querySelectorAll('input'));
      const match = radios.includes(intendedInput)
        ? intendedInput
        : radios.find((radio) => radio instanceof HTMLInputElement && radio.checked);
      if (match instanceof HTMLInputElement) selected.push(match);
    }

    const size = selected.find((el) => el.closest('[data-option-type="size"]'));
    if (size?.dataset.sizePostcard === 'true') {
      const unframed = this.querySelector('[data-option-type="frame"] input[data-frame-unframed="true"]');
      if (unframed instanceof HTMLInputElement) {
        selected = selected.filter((el) => !el.closest('[data-option-type="frame"]'));
        selected.push(unframed);
      }
    }

    const frame = selected.find((el) => el.closest('[data-option-type="frame"]'));
    if (frame?.dataset.frameUnframed === 'true') {
      const noMat = this.querySelector('[data-option-type="mat"] input[data-mat-value="no"]');
      if (noMat instanceof HTMLInputElement) {
        selected = selected.filter((el) => !el.closest('[data-option-type="mat"]'));
        selected.push(noMat);
      }
    }

    return selected
      .map((el) => el.dataset.optionValueId)
      .filter((id) => typeof id === 'string' && id.length > 0);
  }

  /**
   * @param {string} requestUrl
   * @param {AbortSignal} [signal]
   * @returns {Promise<string>}
   */
  #fetchHtml(requestUrl, signal) {
    return fetch(requestUrl, signal ? { signal } : {}).then((response) => response.text());
  }

  /**
   * @param {string} requestUrl
   * @param {Promise<string>} htmlPromise
   */
  #storeCache(requestUrl, htmlPromise) {
    const wrapped = htmlPromise.catch((error) => {
      if (this.#responseCache.get(requestUrl) === wrapped) {
        this.#responseCache.delete(requestUrl);
      }
      throw error;
    });

    if (this.#responseCache.has(requestUrl)) this.#responseCache.delete(requestUrl);
    this.#responseCache.set(requestUrl, wrapped);

    while (this.#responseCache.size > FAST_VARIANT_CACHE_LIMIT) {
      const oldest = this.#responseCache.keys().next().value;
      if (oldest == null) break;
      this.#responseCache.delete(oldest);
    }
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
